import {
  Box,
  Button,
  Group,
  TextInput,
  Textarea,
  ActionIcon,
  Text,
  Badge,
  Select,
  FileInput,
  Image,
  MultiSelect,
} from "@mantine/core";
import Icon from "../../common/Icons/Icons/Icon";
import { QBQuestionFormProps } from "src/interfaces/KnowledgeBase";
import { showImageModal } from "src/components/common/imagePreviewModal/ImagePreviewModal";

export const QBQuestionFormEditor = ({ data }: QBQuestionFormProps) => {
  const {
    isEditing,
    qbConfig,
    qbThemes,
    addQBTheme,
    removeQBTheme,
    addQBTopic,
    removeQBTopic,
    addQBQuestion,
    removeQBQuestion,
    updateQBQuestion,
    updateSelectedTheme,
    updateSelectedTopic,
  } = data;

  if (!qbConfig || !qbThemes) {
    return (
      <Text size="sm" color="dimmed">
        No config available.
      </Text>
    );
  }

  return (
    <Box className="pr-3">
      <div className="flex justify-end">
        <Button variant="light" size="xs" radius="xl" onClick={addQBTheme}>
          <Icon name="plus" size="16px" className="mr-1" /> Add{" "}
          {qbConfig.themeLabel}
        </Button>
      </div>
      {qbThemes.map((theme, themeIndex) => (
        <Box
          key={themeIndex}
          p="md"
          mt="md"
          className="bg-gray-50 rounded-xl shadow-sm"
        >
          <Group
            position="apart"
            className="flex justify-between bg-white p-2 items-end rounded-lg relative"
          >
            <Badge className="py-[13px] px-2 rounded border text-sm">
              {themeIndex + 1}
            </Badge>
            <Select
              label={qbConfig.themeLabel}
              size="xs"
              value={theme.name}
              onChange={(value) => updateSelectedTheme(value!, themeIndex)}
              required
              style={{ flex: 1 }}
              data={
                qbThemes[themeIndex].options?.map((option) => ({
                  value: option,
                  label: option,
                })) || []
              }
              allowDeselect={false}
            />
            {qbThemes.length > 1 && (
              <ActionIcon color="red" onClick={() => removeQBTheme(themeIndex)}>
                <Icon name="trash" size="18px" />
              </ActionIcon>
            )}
          </Group>
          <div className="flex justify-end my-3">
            <Button
              variant="light"
              size="xs"
              radius="xl"
              onClick={() => addQBTopic(themeIndex)}
            >
              <Icon name="plus" size="16px" className="mr-1" /> Add{" "}
              {qbConfig.topicLabel}
            </Button>
          </div>
          <Box className="bg-gray-100 py-3 pl-8 sm:pl-0 pr-3 rounded-lg sm:ml-0 ml-8">
            {theme.topics.map((topic, topicIndex) => (
              <Box
                key={topicIndex}
                className={
                  topicIndex !== 0 ? "border-t-2 border-gray-200 pt-2 mt-2" : ""
                }
              >
                <Group
                  position="apart"
                  className="flex justify-between bg-white p-2 items-end rounded-lg"
                >
                  <Badge className="py-[13px] px-2 rounded border text-sm">
                    {topicIndex + 1}
                  </Badge>
                  <Select
                    label={qbConfig.topicLabel}
                    size="xs"
                    value={topic.name}
                    disabled={qbThemes[themeIndex].name == ""}
                    onChange={(value) => {
                      updateSelectedTopic(value!, themeIndex, topicIndex);
                    }}
                    required
                    style={{ flex: 1 }}
                    data={
                      qbThemes[themeIndex].topics[topicIndex].options?.map(
                        (option) => ({
                          value: option,
                          label: option,
                        })
                      ) || []
                    }
                    allowDeselect={false}
                  />
                  {qbThemes[themeIndex].topics.length > 1 && (
                    <ActionIcon
                      color="red"
                      onClick={() => removeQBTopic(themeIndex, topicIndex)}
                    >
                      <Icon name="trash" size="18px" />
                    </ActionIcon>
                  )}
                </Group>
                <div className="flex justify-end my-3">
                  <Button
                    variant="light"
                    size="xs"
                    radius="xl"
                    onClick={() => addQBQuestion(themeIndex, topicIndex)}
                  >
                    <Icon name="plus" size="16px" className="mr-1" /> Add{" "}
                    {qbConfig.questionLabel}
                  </Button>
                </div>
                <Box className="bg-white p-2 rounded-lg pr-3 sm:ml-0 ml-8">
                  {topic.questions.map((question, questionIndex) => (
                    <Box
                      key={question.uuid}
                      mt="sm"
                      className={
                        questionIndex !== 0
                          ? "border-t-2 border-gray-200 pt-2 mt-2 bg-gray-50 rounded-lg p-2"
                          : "bg-gray-50 p-2 rounded-lg"
                      }
                    >
                      <Group
                        position="apart"
                        className="flex items-end px-2 rounded-lg"
                      >
                        <Badge className="py-[13px] px-2 rounded border text-sm">
                          {questionIndex + 1}
                        </Badge>
                        <TextInput
                          label={qbConfig.questionLabel}
                          labelProps={{ hidden: true }}
                          size="xs"
                          className="bg-transparent"
                          value={question.uuid}
                          hidden
                          onChange={(e) =>
                            updateQBQuestion(themeIndex, topicIndex, questionIndex, {
                              ...question,
                              uuid: e.target.value,
                            })
                          }
                          required
                          style={{ flex: 1 }}
                        />
                        {topic.questions.length > 1 && (
                          <ActionIcon
                            color="red"
                            onClick={() =>
                              removeQBQuestion(themeIndex, topicIndex, questionIndex)
                            }
                          >
                            <Icon name="trash" size="18px" />
                          </ActionIcon>
                        )}
                      </Group>
                      <Select
                        label={qbConfig.itemDemandLabel}
                        size="xs"
                        className="mt-2"
                        value={question.item_demand.value}
                        onChange={(value) =>
                          updateQBQuestion(themeIndex, topicIndex, questionIndex, {
                            ...question,
                            item_demand: {
                              ...question.item_demand,
                              value: value || "",
                            },
                          })
                        }
                        data={qbConfig.itemDemandOptions.map((option) => ({
                          value: option,
                          label: option,
                        }))}
                        required
                      />
                      <TextInput
                        label={qbConfig.answerLabel}
                        size="xs"
                        className="mt-2"
                        value={question.answer}
                        onChange={(e) =>
                          updateQBQuestion(themeIndex, topicIndex, questionIndex, {
                            ...question,
                            answer: e.target.value,
                          })
                        }
                        required
                      />
                      {qbConfig.hasExplanation && (
                        <Textarea
                          label={qbConfig.explanationLabel}
                          size="xs"
                          className="mt-2"
                          value={question.explanation}
                          onChange={(e) =>
                            updateQBQuestion(themeIndex, topicIndex, questionIndex, {
                              ...question,
                              explanation: e.target.value,
                            })
                          }
                          required
                        />
                      )}
                      <div className="flex flex-col mt-2 items-end">
                        {isEditing &&
                          !question.image.name &&
                          question.image?.value && (
                            <div className="w-full flex items-center justify-center">
                              <Image
                                onClick={(event) => {
                                  event.stopPropagation();
                                  showImageModal({
                                    title: "Uploaded File",
                                    imageUrl: question.image.value,
                                    caption: question.image.name,
                                  });
                                }}
                                src={question.image.value}
                                alt={question.image.name}
                                height={200}
                                fit="contain"
                                className="rounded border border-gray-300 cursor-pointer"
                              />
                            </div>
                          )}
                        <label className="block mt-1 w-full">
                          <span className="text-xs">
                            {qbConfig.imageLabel || "Upload image"}{" "}
                            <span className="text-red-500">*</span>
                          </span>

                          <div>
                            <div className="relative border border-dashed border-gray-300 rounded py-4 p-2 text-center cursor-pointer hover:bg-gray-50 overflow-hidden">
                              {!question.image?.file && (
                                <span className="text-xs text-gray-400 absolute inset-0 flex items-center justify-center">
                                  Click to{" "}
                                  {isEditing &&
                                  !question.image.name &&
                                  question.image?.value
                                    ? "Replace"
                                    : "Upload"}{" "}
                                  (JPEG/PNG)
                                </span>
                              )}

                              {question.image?.file && (
                                <span className="text-xs text-gray-600 flex items-center">
                                  {question.image.file.name}
                                </span>
                              )}

                              <FileInput
                                size="xs"
                                accept="image/jpeg, image/png"
                                className="opacity-0 w-full absolute top-0 left-0 cursor-pointer"
                                onChange={(file: File | null) =>
                                  updateQBQuestion(
                                    themeIndex,
                                    topicIndex,
                                    questionIndex,
                                    {
                                      ...question,
                                      image: {
                                        name: file?.name || "",
                                        value: question.uuid,
                                        file: file,
                                      },
                                    }
                                  )
                                }
                                required
                                value={question.image?.file}
                              />
                            </div>
                          </div>
                        </label>
                      </div>

                      <TextInput
                        label={`${qbConfig.imageLabel} URL`}
                        size="xs"
                        hidden
                        labelProps={{ hidden: true }}
                        className="mt-2"
                        value={question.image.value}
                        onChange={(e) =>
                          updateQBQuestion(themeIndex, topicIndex, questionIndex, {
                            ...question,
                            image: {
                              ...question.image,
                              value: e.target.value,
                            },
                          })
                        }
                        required
                      />
                      <MultiSelect
                        label={qbConfig.learningObjectiveLabel}
                        size="xs"
                        className="mt-2"
                        placeholder="Select topic"
                        value={question.objectives}
                        onChange={(value) =>
                          updateQBQuestion(themeIndex, topicIndex, questionIndex, {
                            ...question,
                            objectives: value,
                          })
                        }
                        data={
                          qbThemes[themeIndex].topics[
                            topicIndex
                          ].learning_objectives_options?.map((option) => ({
                            value: option,
                            label: option,
                          })) || []
                        }
                        disabled={qbThemes[themeIndex].topics[topicIndex].name === ""}
                        required
                      />
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
};
